import React, { useContext, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ApplicationContext } from "./context/GlobalState";
import { extractLocaleFromPath } from "./i18n";
import Routes from "./routes/Routes";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import HipaaBreachBanner from "./components/HipaaBreachBanner/HipaaBreachBanner";
// LocationGate (the auto-popping first-visit clinic modal) was RETIRED on
// 2026-08-29 (owner order): visitors were being interrupted every visit and
// complained. Clinic selection is now opt-in ONLY — the visitor clicks the
// header LocationSelector button to open the picker. The picker still supports
// "Use my location" (device GPS → thor-geo /geo/resolve) inside the same modal,
// but nothing pops unsolicited. The component file is kept for reference and
// its unit tests still run so the mandatory-blocking regression path is
// documented; it is no longer mounted anywhere.
import { trackPhoneClick, trackCtaClick, trackLocationClick, markAttorneyReferral } from "./utils/analytics";
import { applyBrandTheme } from "./utils/applyBrandTheme";
import "./styles/glassmorphism.css";
import "./App.css";

function App() {
  const { state, dispatch } = useContext(ApplicationContext);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Inject brand colors as CSS custom properties (initial paint is handled
  // synchronously in GlobalState.js; this keeps runtime brand changes live)
  useEffect(() => {
    applyBrandTheme(state.brand);
  }, [state.brand]);

  // Sync locale from URL into global state on every navigation
  useEffect(() => {
    const { locale: urlLocale } = extractLocaleFromPath(location.pathname);
    // Keep <html lang> in sync globally (replaces per-container Helmet hacks;
    // prerendered files already ship the correct lang — this keeps hydration honest).
    if (document.documentElement.lang !== urlLocale) {
      document.documentElement.lang = urlLocale;
    }
    if (urlLocale !== state.locale) {
      dispatch({ type: "SET_LOCALE", payload: urlLocale });
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile menu on route change only (not on menu state change).
  // Hash-aware scroll (2026-07-21 audit M5): honor #anchors (e.g. /services#spine)
  // instead of always jumping to top; retry once after paint for lazy chunks.
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      dispatch({ type: "SET_MOBILE_MENU", payload: false });
      if (location.hash) {
        const id = location.hash.slice(1);
        requestAnimationFrame(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView();
          else setTimeout(() => document.getElementById(id)?.scrollIntoView(), 300);
        });
      } else {
        window.scrollTo(0, 0);
      }
    }
  }, [location.pathname, location.hash, dispatch]);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (state.isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [state.isMobileMenuOpen]);

  // Responsive breakpoint detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 1024;
      dispatch({ type: "SET_IS_MOBILE", payload: isMobile });
      if (window.innerWidth > 1024) {
        dispatch({ type: "SET_MOBILE_MENU", payload: false });
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [dispatch]);

  // Delegated GA4 event capture (spec v2.1.0). One listener covers the whole app
  // so individual components don't each need wiring. Conventions:
  //   - <a href="tel:...">                       -> pseo_phone_click (KEY)
  //   - any element with data-pseo-attorney      -> marks lead_channel=attorney_referral + cta_click
  //   - any element with data-pseo-cta="id"       -> pseo_cta_click (data-pseo-cta-text optional)
  //   - any element with data-pseo-location       -> pseo_location_click
  // Video play is wired directly where players mount. Analytics never throws.
  useEffect(() => {
    const onClick = (e) => {
      try {
        const el = e.target && e.target.closest
          ? e.target.closest('a[href^="tel:"],[data-pseo-attorney],[data-pseo-cta],[data-pseo-location]')
          : null;
        if (!el) return;
        const text = (el.getAttribute('data-pseo-cta-text') || el.textContent || '').trim().slice(0, 90);
        if (el.hasAttribute('data-pseo-attorney')) {
          markAttorneyReferral();
          trackCtaClick({ cta_id: el.getAttribute('data-pseo-cta') || 'attorney_referral', cta_text: text, lead_channel: 'attorney_referral' });
          return;
        }
        const href = el.getAttribute && el.getAttribute('href');
        if (href && href.indexOf('tel:') === 0) {
          trackPhoneClick({ cta_id: el.getAttribute('data-pseo-cta') || 'phone', cta_text: text || href.replace('tel:', '') });
          return;
        }
        if (el.hasAttribute('data-pseo-location')) {
          trackLocationClick({ cta_id: el.getAttribute('data-pseo-cta') || 'location', cta_text: text });
          return;
        }
        if (el.hasAttribute('data-pseo-cta')) {
          trackCtaClick({ cta_id: el.getAttribute('data-pseo-cta'), cta_text: text });
        }
      } catch (err) { /* analytics must never break clicks */ }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  return (
    <div className="app">
      {/* HIPAA third-party data breach notice (CareCloud) — 2026-09-14 legal
          directive from Trinity Medical Management. Mounted ABOVE <Header /> so
          it renders above the nav on every route across all three CE brands
          (gaspineortho.com, apexosn.com, apexosntn.com). Dismiss state is
          sessionStorage — notice returns each visit until legal signals removal. */}
      <HipaaBreachBanner />
      <Header />
      <main className="main-content">
        <Routes />
      </main>
      <Footer />
      {/* First-visit clinic gate RETIRED 2026-08-29 (owner order): the modal
          only opens when the visitor clicks the header LocationSelector.
          Location is now inferred from URL (/:city/... routes) or from a
          previously-stored choice; unknown state simply falls back to brand
          defaults for internal service links (see Header.js). */}
    </div>
  );
}

export default App;
